// UI strings for trimming and deleting own recordings and the playback volume (2026-10-06,
// src/recordings/). Finnish (default) and English side by side. Style: docs/FINNISH-STYLE.md.
import { defineArea } from '../area';

const fiNum = (x: number) => x.toFixed(2).replace('.', ',');
const enNum = (x: number) => x.toFixed(2);

export const recordingEditFi = {
  trim: 'Leikkaa',
  title: 'Leikkaa äänite',
  waveform: 'Äänitteen aaltomuoto',
  startHandle: 'Äänitteen alku',
  endHandle: 'Äänitteen loppu',
  selStart: 'Valinnan alku',
  selEnd: 'Valinnan loppu',
  seconds: (x: number) => `${fiNum(x)} s`,
  span: (a: number, b: number) => `${fiNum(a)}–${fiNum(b)} s`,
  length: (x: number) => `Kesto ${fiNum(x)} s`,
  playResult: 'Kuuntele tulos',
  playSelection: 'Kuuntele valinta',
  stop: 'Pysäytä',
  select: 'Valitse kohta',
  cut: 'Poista kohta',
  cuts: 'Poistetut kohdat',
  keepCut: (a: number, b: number) => `Palauta kohta ${fiNum(a)}–${fiNum(b)} s`,
  keep: 'Palauta',
  silence: 'Tyhjennä hiljaisuus alusta ja lopusta',
  silenceNone: 'Alusta ja lopusta ei löytynyt hiljaisuutta.',
  undo: 'Kumoa',
  reset: 'Aloita alusta',
  save: 'Tallenna',
  /** A fresh take's trim is done (the take is sent with its own button). */
  apply: 'Valmis',
  cancel: 'Peruuta',
  original: 'Palauta alkuperäinen',
  loading: 'Avataan äänitettä…',
  loadFailed: 'Äänitettä ei voitu avata. Leikkaaminen tarvitsee yhteyden, ellei äänite ole jo tällä laitteella.',
  decodeFailed: 'Tämä selain ei osaa avata äänitettä leikattavaksi.',
  tooShort: 'Äänitteestä jäisi liian vähän jäljelle.',
  saving: 'Tallennetaan…',
  trimmed: 'leikattu',
  pending: 'Leikattu versio lähtee, kun yhteys toimii.',
  delete: 'Poista äänite',
  deleteConfirm: 'Poistetaanko äänite?',
  deleteYes: 'Poista',
  deleted: 'Äänite poistettu.',
  gone: 'Äänite poistettu',
  playFailed: 'Äänitettä ei voitu toistaa.',
  volume: 'Äänenvoimakkuus',
  volumeSection: 'Ääni',
  volumeValue: (n: number) => `${n}\u00a0%`,
  volumeExample:
    'Koskee tällä laitteella kaikkia sovelluksen ääniä: Petersonin äänitteitä, sarjan repliikkejä ja teidän äänityksiänne. Hiljaiset ja kovat äänitykset tasoitetaan samalle tasolle.',
};

export type RecordingEditMessages = typeof recordingEditFi;

export const recordingEditEn: RecordingEditMessages = {
  trim: 'Trim',
  title: 'Trim the recording',
  waveform: 'Waveform of the recording',
  startHandle: 'Start of the recording',
  endHandle: 'End of the recording',
  selStart: 'Start of the selection',
  selEnd: 'End of the selection',
  seconds: (x) => `${enNum(x)} s`,
  span: (a, b) => `${enNum(a)}–${enNum(b)} s`,
  length: (x) => `Length ${enNum(x)} s`,
  playResult: 'Play the result',
  playSelection: 'Play the selection',
  stop: 'Stop',
  select: 'Select a part',
  cut: 'Remove part',
  cuts: 'Removed parts',
  keepCut: (a, b) => `Put back ${enNum(a)}–${enNum(b)} s`,
  keep: 'Put back',
  silence: 'Clear silence at the start and end',
  silenceNone: 'No silence found at the start or end.',
  undo: 'Undo',
  reset: 'Start over',
  save: 'Save',
  apply: 'Done',
  cancel: 'Cancel',
  original: 'Restore the original',
  loading: 'Opening the recording…',
  loadFailed: 'The recording could not be opened. Trimming needs a connection unless the recording is already on this device.',
  decodeFailed: 'This browser cannot open the recording for trimming.',
  tooShort: 'Too little of the recording would remain.',
  saving: 'Saving…',
  trimmed: 'trimmed',
  pending: 'The trimmed version is sent once the connection works.',
  delete: 'Delete recording',
  deleteConfirm: 'Delete this recording?',
  deleteYes: 'Delete',
  deleted: 'Recording deleted.',
  gone: 'Recording deleted',
  playFailed: 'The recording could not be played.',
  volume: 'Volume',
  volumeSection: 'Sound',
  volumeValue: (n) => `${n} %`,
  volumeExample:
    'Applies to every sound in the app on this device: Peterson’s recordings, show lines and your own recordings. Quiet and loud recordings are levelled to the same level.',
};

// Messages.recEdit (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('recEdit', recordingEditFi, recordingEditEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof recordingEditFi> {}
}
